// hooks/use-analytics.ts
import { useState } from "react";
import { ActiveUsersApiResponse } from '../../../pages/Analytics';
import { formatDate } from '../../../utils/helpers';
import { useGetActiveUsersData } from '../../../apis/analytics/get-active-users';
import { useGetFirstOpenData } from '../../../apis/analytics/get-first-open-data';
type AnalyticsType = {
    dimensions: string;
    metrics: number;
};

const formatChartData = (
    data: ActiveUsersApiResponse,
    dimensionIndex = 0
): AnalyticsType[] => {
    const formatted = data.map((item) => {
        const raw = item.dimensions[dimensionIndex];
        return {
            dimensions: `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6)}`,
            metrics: parseInt(item.metrics[0]),
        };
    });
    return formatted.sort((a, b) => a.dimensions.localeCompare(b.dimensions));
};

const useAnalyticsDashboard = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const [activeUsersStart, setActiveUsersStart] = useState(formatDate(sevenDaysAgo.toString()));
    const [activeUsersEnd, setActiveUsersEnd] = useState(formatDate(today.toString()));
    const [activeUsersFilter, setActiveUsersFilter] = useState({
        startDate: formatDate(sevenDaysAgo.toString()),
        endDate: formatDate(today.toString()),
    });

    const [firstOpenStart, setFirstOpenStart] = useState(formatDate(sevenDaysAgo.toString()));
    const [firstOpenEnd, setFirstOpenEnd] = useState(formatDate(today.toString()));
    const [firstOpenFilter, setFirstOpenFilter] = useState({
        startDate: formatDate(sevenDaysAgo.toString()),
        endDate: formatDate(today.toString()),
    });

    const { data: activeUsersData, isFetching: activeUsersLoading } = useGetActiveUsersData(activeUsersFilter);
    const { data: firstOpenData, isFetching: firstOpenLoading } = useGetFirstOpenData(firstOpenFilter);

    const activeUsers = activeUsersData ? formatChartData(activeUsersData) : [];
    const firstOpen = firstOpenData
        ? formatChartData(
            firstOpenData.filter((item) => item.dimensions[0] === "first_open"),
            1
        )
        : [];

    const handleActiveUsersStart = (value: Date | null | undefined) =>
        setActiveUsersStart(formatDate(value?.toString() || ""));

    const handleActiveUsersEnd = (value: Date | null | undefined) =>
        setActiveUsersEnd(formatDate(value?.toString() || ""));

    const handleActiveUsersFilter = () =>
        setActiveUsersFilter({ startDate: activeUsersStart, endDate: activeUsersEnd });

    const handleFirstOpenStart = (value: Date | null | undefined) =>
        setFirstOpenStart(formatDate(value?.toString() || ""));

    const handleFirstOpenEnd = (value: Date | null | undefined) =>
        setFirstOpenEnd(formatDate(value?.toString() || ""));

    const handleFirstOpenFilter = () =>
        setFirstOpenFilter({ startDate: firstOpenStart, endDate: firstOpenEnd });

    return {
        activeUsers,
        activeUsersLoading,
        activeUsersStart,
        activeUsersEnd,
        handleActiveUsersStart,
        handleActiveUsersEnd,
        handleActiveUsersFilter,
        firstOpen,
        firstOpenLoading,
        firstOpenStart,
        firstOpenEnd,
        handleFirstOpenStart,
        handleFirstOpenEnd,
        handleFirstOpenFilter,
    };
};

export default useAnalyticsDashboard;